import { useState, useMemo, useEffect } from "react";
import {
    FormControl,
    TextField,
    Button,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
} from "@mui/material";
import { DateTimeField, LocalizationProvider } from "@mui/x-date-pickers";
import makeStyles from "@mui/styles/makeStyles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useAppSelector, useAppDispatch } from "./store/hooks";
import {
    selectDeviceData,
    selectDeviceDataDatasetVersionDttm,
    selectCurrentClient,
    selectCurrentProject,
    loadLatestDeviceDataDataset,
} from "./store/reducers/deviceLinkingAppStoreSlice";
import { WeatherSensor } from "../types";
import { calibrationService } from "../../../../services/CalibrationService";
import { showGlobalErrorToast } from "../../../../utils/GlobalToast";
import ErrorText from "./ErrorText";

dayjs.extend(utc);

const useStyles = makeStyles(() => ({
    formControlContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        padding: "16px",
    },
    formButtons: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "8px",
        marginTop: "16px",
    },
    confirmationSection: {
        backgroundColor: "#f5f5f5",
        padding: "16px",
        borderRadius: "4px",
        marginTop: "16px",
    },
    inheritedField: {
        display: "flex",
        justifyContent: "space-between",
        padding: "4px 0",
    },
    warningText: {
        color: "#ed6c02",
        fontSize: "0.9em",
        marginTop: "8px",
    },
}));

type ReplaceWeatherSensorFormProps = {
    className?: string;
    onClose?: () => void;
    initialSensorSn?: string;
};

export default function ReplaceWeatherSensorForm(props: ReplaceWeatherSensorFormProps): JSX.Element {
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const deviceData = useAppSelector(selectDeviceData);
    const deviceDatasetVersionDttm = useAppSelector(selectDeviceDataDatasetVersionDttm);
    const client = useAppSelector(selectCurrentClient);
    const project = useAppSelector(selectCurrentProject);

    const [oldSensorSn, setOldSensorSn] = useState<string>(props.initialSensorSn || "");
    const [endDttm, setEndDttm] = useState<dayjs.Dayjs | null>(dayjs.utc());
    const [newSensorSn, setNewSensorSn] = useState<string>("");
    const [startDttm, setStartDttm] = useState<dayjs.Dayjs | null>(dayjs.utc());
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const weatherSensors = deviceData?.data.weather_sensors || [];

    // Find the old sensor to display inherited fields
    const oldSensor = useMemo(() => {
        return weatherSensors.find((sensor) => sensor.device_sn === oldSensorSn);
    }, [oldSensorSn, weatherSensors]);

    // Validation: Old sensor serial number must be selected
    const oldSensorSnError = useMemo(() => {
        return oldSensorSn === "";
    }, [oldSensorSn]);

    // Validation: End datetime must be set and not default
    const endDttmError = useMemo(() => {
        if (!endDttm) return true;
        const now = dayjs.utc();
        return Math.abs(now.diff(endDttm, "minute")) < 2;
    }, [endDttm]);

    // Validation: New sensor serial number must be present
    const newSensorSnError = useMemo(() => {
        return !newSensorSn || newSensorSn === "";
    }, [newSensorSn]);

    const newSensorSnErrorMessage = useMemo(() => {
        if (!newSensorSn || newSensorSn === "") return "New Serial Number is required";
        return "";
    }, [newSensorSn]);

    // Warning: Serial number format (not blocking)
    const newSensorSnFormatWarning = useMemo(() => {
        if (!newSensorSn || newSensorSn === "") return false;
        return !/^A41\d{5}$/.test(newSensorSn);
    }, [newSensorSn]);

    // Validation: Start datetime must be set and not default
    const startDttmError = useMemo(() => {
        if (!startDttm) return true;
        const now = dayjs.utc();
        return Math.abs(now.diff(startDttm, "minute")) < 2;
    }, [startDttm]);

    // Validation: Start datetime must be after end datetime
    const dateOrderError = useMemo(() => {
        if (!startDttm || !endDttm) return false;
        return startDttm.isBefore(endDttm) || startDttm.isSame(endDttm);
    }, [startDttm, endDttm]);

    // Validation: New sensor must not already exist
    const newSensorExistsError = useMemo(() => {
        if (!newSensorSn) return false;
        return weatherSensors.some((sensor) => sensor.device_sn === newSensorSn);
    }, [newSensorSn, weatherSensors]);

    const formIsValid =
        !oldSensorSnError &&
        !endDttmError &&
        !newSensorSnError &&
        !startDttmError &&
        !dateOrderError &&
        !newSensorExistsError;

    const handleSubmit = () => {
        if (!formIsValid) return;
        setShowConfirmationDialog(true);
    };

    const handleConfirmSubmit = async () => {
        setShowConfirmationDialog(false);
        setIsSubmitting(true);
        setError(null);

        console.log(
            "ReplaceWeatherSensorForm: Submitting with version (optimistic locking):",
            deviceDatasetVersionDttm
        );
        console.log("ReplaceWeatherSensorForm: Payload:", {
            old_sn: oldSensorSn,
            end_dttm: endDttm?.toISOString(),
            new_sn: newSensorSn,
            start_dttm: startDttm?.toISOString(),
        });

        try {
            // Pass the current dataset version for optimistic locking (backend requires this even though the operation modifies the dataset)
            await calibrationService.submitDeviceChange(client, project, deviceDatasetVersionDttm, {
                replace_weather_sensor: {
                    old_sn: oldSensorSn,
                    end_dttm: endDttm?.toISOString(),
                    new_sn: newSensorSn,
                    start_dttm: startDttm?.toISOString(),
                },
            });

            // Success - show message and close form
            alert(
                `Weather sensor replacement successful!\n\nOld sensor ${oldSensorSn} has been decommissioned.\nNew sensor ${newSensorSn} has been added with all inherited physical characteristics.`
            );

            // Reset form
            setOldSensorSn("");
            setEndDttm(dayjs.utc());
            setNewSensorSn("");
            setStartDttm(dayjs.utc());

            if (props.onClose) {
                props.onClose();
            }
        } catch (err) {
            console.error("Weather sensor replacement error:", err);
            // Try to extract the most useful error message
            let errorMessage = "Unknown error occurred";
            if (typeof err === "string") {
                errorMessage = err;
            } else if (err && typeof err === "object") {
                // Try different ways to get the error message
                errorMessage = JSON.stringify(err, null, 2);
            }
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={`${classes.formControlContainer} ${props.className || ""}`}>
            <Typography variant="h5">Replace Weather Sensor</Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
                Replace an existing weather sensor with a new one at the SAME physical location. The new sensor will
                automatically inherit all physical characteristics (position, elevation, mounting configuration) from
                the old sensor.
            </Typography>
            <Typography variant="body2" style={{ color: "#ed6c02", fontWeight: 500 }} paragraph>
                IMPORTANT: Only use replacement when the new sensor is installed within a breadbox-sized distance from
                the original location. If the sensor is moved to a significantly different location, deprecate the old
                sensor using the End Date field in the edit form instead.
            </Typography>

            {isSubmitting ? (
                <Box display="flex" justifyContent="center" padding={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <FormControl>
                        <InputLabel htmlFor="old-sensor-sn">Old Sensor Serial Number</InputLabel>
                        <Select
                            id="old-sensor-sn"
                            value={oldSensorSn}
                            onChange={(event) => setOldSensorSn(event.target.value)}
                            error={oldSensorSnError && oldSensorSn !== ""}
                        >
                            {weatherSensors.map((sensor) => (
                                <MenuItem key={sensor.device_sn} value={sensor.device_sn}>
                                    {sensor.device_sn} (Site {sensor.site_id})
                                </MenuItem>
                            ))}
                        </Select>
                        {oldSensorSnError && oldSensorSn !== "" && (
                            <span style={{ color: "red", fontSize: "0.8em" }}>
                                Old sensor serial number is required
                            </span>
                        )}
                    </FormControl>

                    <FormControl>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimeField
                                label="Old Sensor End Date/Time (UTC)"
                                value={endDttm}
                                onChange={(newDate) => setEndDttm(newDate)}
                                slotProps={{
                                    textField: {
                                        error: endDttmError,
                                        helperText: endDttmError
                                            ? "End date must be set to the actual decommission time (not the default current time)"
                                            : "",
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </FormControl>

                    <FormControl>
                        <InputLabel htmlFor="new-sensor-sn">New Sensor Serial Number</InputLabel>
                        <TextField
                            id="new-sensor-sn"
                            value={newSensorSn}
                            onChange={(event) => setNewSensorSn(event.target.value.trim())}
                            error={newSensorSnError && newSensorSn !== ""}
                            helperText={newSensorSnErrorMessage}
                            placeholder="A4101479"
                        />
                        {newSensorSnFormatWarning && (
                            <span style={{ color: "orange", fontSize: "0.8em" }}>
                                Warning: Serial Number should be in format A41XXXXX (e.g., A4101479)
                            </span>
                        )}
                        {newSensorExistsError && (
                            <span style={{ color: "red", fontSize: "0.8em" }}>
                                Sensor with serial number {newSensorSn} already exists in the dataset
                            </span>
                        )}
                    </FormControl>

                    <FormControl>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimeField
                                label="New Sensor Start Date/Time (UTC)"
                                value={startDttm}
                                onChange={(newDate) => setStartDttm(newDate)}
                                slotProps={{
                                    textField: {
                                        error: startDttmError || dateOrderError,
                                        helperText: dateOrderError
                                            ? "Start date must be after end date"
                                            : startDttmError
                                            ? "Start date must be set to the actual installation time (not the default current time)"
                                            : "",
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </FormControl>

                    {oldSensor && (
                        <Box className={classes.confirmationSection}>
                            <Typography variant="subtitle2" gutterBottom>
                                Inherited Physical Characteristics
                            </Typography>
                            <Typography variant="caption" color="textSecondary" paragraph>
                                The new sensor will automatically inherit these values from {oldSensorSn}:
                            </Typography>
                            <div className={classes.inheritedField}>
                                <span>Position:</span>
                                <strong>
                                    {oldSensor?.position?.latitude?.toFixed(6)},{" "}
                                    {oldSensor?.position?.longitude?.toFixed(6)}
                                </strong>
                            </div>
                            <div className={classes.inheritedField}>
                                <span>Elevation:</span>
                                <strong>{oldSensor?.elevation} m</strong>
                            </div>
                            <div className={classes.inheritedField}>
                                <span>Height Above Ground:</span>
                                <strong>{oldSensor?.height_above_ground} m</strong>
                            </div>
                            <div className={classes.inheritedField}>
                                <span>Anemometer Heading:</span>
                                <strong>{oldSensor?.anemometer_heading}°</strong>
                            </div>
                            <div className={classes.inheritedField}>
                                <span>Mounting Arm Length:</span>
                                <strong>{oldSensor?.mounting_arm_length} m</strong>
                            </div>
                            <div className={classes.inheritedField}>
                                <span>Mounting Arm Orientation:</span>
                                <strong>{oldSensor?.mounting_arm_orientation_heading}°</strong>
                            </div>
                            <div className={classes.inheritedField}>
                                <span>Site ID:</span>
                                <strong>{oldSensor?.site_id}</strong>
                            </div>
                            <div className={classes.inheritedField}>
                                <span>Device Type:</span>
                                <strong>{oldSensor?.device_type}</strong>
                            </div>
                        </Box>
                    )}

                    <div className={classes.formButtons}>
                        {props.onClose && (
                            <Button onClick={props.onClose} variant="outlined">
                                Cancel
                            </Button>
                        )}
                        <Button onClick={handleSubmit} disabled={!formIsValid} variant="contained" color="primary">
                            Replace Sensor
                        </Button>
                    </div>

                    {error && <ErrorText error={error} />}
                </>
            )}

            <Dialog open={showConfirmationDialog} onClose={() => setShowConfirmationDialog(false)}>
                <DialogTitle>Confirm Weather Sensor Replacement</DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        You are about to replace weather sensor <strong>{oldSensorSn}</strong> with{" "}
                        <strong>{newSensorSn}</strong>.
                    </Typography>
                    <Typography paragraph>
                        <strong>What will happen:</strong>
                    </Typography>
                    <ul>
                        <li>
                            Old sensor {oldSensorSn} will be decommissioned with end date:{" "}
                            {endDttm?.format("YYYY-MM-DD HH:mm:ss")} UTC
                        </li>
                        <li>
                            New sensor {newSensorSn} will be added with start date:{" "}
                            {startDttm?.format("YYYY-MM-DD HH:mm:ss")} UTC
                        </li>
                        <li>The new sensor will inherit all physical characteristics from the old sensor</li>
                        <li>Both sensors will remain in the DeviceDataset (old with end_dttm, new without)</li>
                        <li>In MasterData, both sensors will share the same weather_site</li>
                    </ul>
                    <Typography color="textSecondary" variant="body2">
                        This operation cannot be easily undone. Are you sure you want to proceed?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirmationDialog(false)}>Cancel</Button>
                    <Button onClick={handleConfirmSubmit} color="primary" variant="contained">
                        Confirm Replacement
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
